import { Application, DisplayObject, InteractionEvent, Point } from "pixi.js"
import { IMouse } from "../../../../client_models"

interface MouseState {
    position: Point
    buttons: Map<string, boolean>
}

type Listener = (mouseState: MouseState) => void

export class Mouse implements IMouse {
    public static listeners: Listener[] = []

    public static state: MouseState = {
        position: new Point(),
        buttons: new Map(),
    }

    addListener = (listener: (mouseState: MouseState) => void) => {
        if (Mouse.listeners.indexOf(listener) < 0) {
            Mouse.listeners.push(listener)
        }
    }

    removeListener = (listener: (mouseState: MouseState) => void) => {
        Mouse.listeners = Mouse.listeners.filter(l => l != listener)
    }

    updateButton = (buttonId: string, val: boolean) => {
        Mouse.state.buttons.set(buttonId, val)
    }

    state = (): MouseState => {
        return Mouse.state
    }

    // The world's own layers. A press that lands on anything else on the stage is a press on the HUD,
    // and the world must not hear about it -- see pressIsOnGui.
    private static app?: Application
    private static worldRoots: DisplayObject[] = []
    // True while a press that STARTED on the HUD is still held. The release has to be swallowed too,
    // or letting go over open ground would arrive as a world click the player never made.
    private static guiPress: boolean = false

    public static setWorldRoots(roots: DisplayObject[]) {
        Mouse.worldRoots = roots.filter(Boolean)
    }

    /**
     * Did this press land on the HUD rather than on the world?
     *
     * Button presses are bound to the DOCUMENT, not to the stage, so pixi never hit-tests them and
     * every click reached the player controller wherever it landed: pressing Items, or the skip button
     * on the intro prompt, swung the player's weapon at whatever was behind it. Hovering used to be
     * the only defence (GuiManager.mouseOverControl), which meant every control had to remember to opt
     * in, and most never did.
     *
     * Asking pixi what is under the pointer settles it in one place instead. Nothing under the pointer,
     * or something belonging to a world layer, is a world press as before.
     */
    private static pressIsOnGui(): boolean {
        const interaction: any = (Mouse.app?.renderer as any)?.plugins?.interaction
        if (!interaction?.hitTest || Mouse.worldRoots.length === 0) {
            return false
        }
        const hit = interaction.hitTest(Mouse.state.position)
        if (!hit || hit === Mouse.app?.stage) {
            return false
        }
        for (let node: DisplayObject | null = hit; node; node = node.parent) {
            if (Mouse.worldRoots.indexOf(node) >= 0) {
                return false
            }
        }
        return true
    }

    /**
     * What a DOM-level press landed on, in the display tree, or null for empty canvas.
     *
     * Presses are bound to the DOCUMENT rather than the stage (see pressIsOnGui), so pixi never
     * hit-tests them and a widget has no way of its own to tell whether a click belongs to it. This
     * answers that for any listener that needs it -- e.g. the HUD dismissing an open command wheel
     * on a press that landed anywhere else.
     *
     * `undefined` means the question can't be answered here (no interaction plugin, i.e. headless
     * tests). Callers should read that as "don't act", never as "landed on nothing".
     */
    public static pressTarget(at: { clientX: number; clientY: number }): DisplayObject | null | undefined {
        const interaction: any = (Mouse.app?.renderer as any)?.plugins?.interaction
        if (!interaction?.hitTest || !interaction?.mapPositionToPoint) {
            return undefined
        }
        const point = new Point()
        interaction.mapPositionToPoint(point, at.clientX, at.clientY)
        return interaction.hitTest(point) || null
    }

    public static initialize(app: Application) {
        Mouse.app = app
        app.stage.on("pointermove", Mouse.movePointer)
        // The canvas owns secondary clicks, including trackpad two-finger taps.
        app.view.addEventListener("contextmenu", event => event.preventDefault())
        document.addEventListener("mousedown", Mouse.pointerDown)
        document.addEventListener("mouseup", Mouse.pointerUp)
    }
    private static movePointer(e: InteractionEvent): void {
        Mouse.state.position = e.data.global
        Mouse.listeners.forEach(listener => listener(Mouse.state))
    }

    private static pointerUp(e: MouseEvent): void {
        // The release of a press that began on the HUD is swallowed with it, and the button is left
        // false so nothing downstream thinks it is still being held.
        if (Mouse.guiPress) {
            Mouse.guiPress = false
            Mouse.state.buttons.set(e.button.toString(), false)
            return
        }
        Mouse.state.buttons.set(e.button.toString(), false)
        Mouse.listeners.forEach(listener => listener(Mouse.state))
    }

    private static pointerDown(e: MouseEvent): void {
        if (Mouse.pressIsOnGui()) {
            // Never set the button: the world should not see this press at all, and a pointermove
            // arriving while it is held would otherwise read as a swing.
            Mouse.guiPress = true
            Mouse.state.buttons.set(e.button.toString(), false)
            return
        }
        Mouse.state.buttons.set(e.button.toString(), true)
        Mouse.listeners.forEach(listener => listener(Mouse.state))
    }
}
